import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError } from "@/lib/apiClient";
import { downloadUrl } from "@/lib/download";
import { CaptainPlan } from "@/components/CaptainPlan";
import { SessionRoom } from "@/components/SessionRoom";
import { SendMaterialButton, SendNextMaterialButton } from "@/components/SendMaterialButton";
import { RemindersOffBanner } from "@/components/RemindersOffBanner";
import { SendLog } from "@/components/SendLog";
import { toast } from "sonner";
import { CalendarClock, Crown, QrCode, X, Download, Copy, Check, Clock, Presentation } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function PptxDownload({ club, week, url }) {
  const [dl, setDl] = useState(false);
  const go = async () => {
    setDl(true);
    api.post("/weekly-sessions/material-opened", { club, week }).catch(() => {});
    try { await downloadUrl(`${API}${url}`, `Sessao-${week}.pptx`); }
    catch (err) { toast.error(err.message || "Não foi possível descarregar a apresentação"); }
    setDl(false);
  };
  return (
    <button onClick={go} disabled={dl} className="kw-btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1"
      data-testid={`session-pptx-${club}`}>
      {dl ? <Clock className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Apresentação
    </button>
  );
}

export default function WeeklySessions() {
  const [week, setWeek] = useState(null);
  const [data, setData] = useState(null);

  const load = (w) => api.get(`/weekly-sessions${w ? `?week=${w}` : ""}`)
    .then((r) => { setData(r.data); setWeek(r.data.week); })
    .catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="kw-overline">A carregar…</div>;
  return (
    <div className="space-y-8 kw-fade-up" data-testid="weekly-sessions-page">
      {data.reminders_off && <RemindersOffBanner />}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">Presenças e pontualidade</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter">
            Reuniões <span className="text-[#E50000]">semanais</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-3xl">
            Cada reunião da Configuração gera sozinha a sessão com QR (1h30). O QR segue por email ao Secretário
            e ao Capitão 24h e 2h antes. Só os coopetidores contam: à hora certa +5, depois presente sem +5, sem QR falta.
          </p>
        </div>
        <div className="kw-card px-4 py-3">
          <label className="kw-overline text-[10px] block mb-1">Semana</label>
          <input type="number" min="1" max={data.weeks_total} className="kw-input !w-24 !py-1.5" value={week || ""}
            onChange={(e) => { const w = Number(e.target.value); setWeek(w); if (w >= 1) load(w); }}
            data-testid="weekly-sessions-week" />
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {data.clubs.map((c) => <SessionCard key={c.club} c={c} onChange={() => load(week)} />)}
      </div>
    </div>
  );
}

function SessionCard({ c, onChange }) {
  const s = c.session;
  const setCaptain = async (player_id) => {
    try {
      if (player_id) await api.post("/coach/captain", { club: c.club, week_number: c.week, player_id });
      else await api.delete(`/coach/captain?club=${encodeURIComponent(c.club)}&week=${c.week}`);
      toast.success(player_id ? "Capitão escolhido — foi avisado por email" : "Capitão removido");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="kw-card p-5 space-y-4" data-testid={`session-card-${c.club}`}>
      <div className="flex items-start gap-3">
        <CalendarClock className="w-5 h-5 text-[#E50000] mt-1" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-2xl font-black uppercase">{c.club}</div>
          <div className="text-xs text-[#A3A3A3]" data-testid={`session-when-${c.club}`}>
            {s ? s.when_label : "Sem sessão: o clube não tem horário na Configuração, ou a reunião desta semana já passou sem QR."}
          </div>
        </div>
        {s && <span className="text-[10px] uppercase tracking-widest text-[#737373]">{s.present_count}/{s.total} presentes</span>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Crown className="w-4 h-4 text-[#EAB308]" />
        {c.can_manage ? (
          <select className="kw-input !py-1.5 md:!w-64 text-xs" value={c.captain?.id || ""}
            onChange={(e) => setCaptain(e.target.value)} data-testid={`session-captain-${c.club}`}>
            <option value="">— Sem Capitão —</option>
            {c.candidates.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : (
          <span className="text-sm">{c.captain ? c.captain.name : "Sem Capitão"}</span>
        )}
        {s && <QrButton s={s} club={c.club} />}
        {s && c.can_manage && <SessionRoom s={s} club={c.club} onChange={onChange} />}
        {c.can_manage && <SendNextMaterialButton club={c.club} week={c.week} title={c.material?.title} onSent={onChange} />}
        {s && !c.can_manage && s.room_name && <span className="text-xs text-[#A3A3A3]">Sala: {s.room_name}</span>}
      </div>
      {c.material ? (
        <div className="flex flex-wrap items-center gap-2 text-xs border-t border-[#222] pt-3" data-testid={`session-material-${c.club}`}>
          <Presentation className="w-4 h-4 text-[#E50000]" />
          <span className="text-[#A3A3A3] truncate max-w-[220px]">{c.material.title}</span>
          <PptxDownload club={c.club} week={c.week} url={c.material.pptx_url} />
          {c.material.audio_url && <audio controls className="h-8" src={`${API}${c.material.audio_url}`} />}
          {c.can_manage && s && <SendMaterialButton club={c.club} week={c.week} onSent={onChange} />}
        </div>
      ) : (
        <div className="text-[11px] text-[#555] border-t border-[#222] pt-3">
          Sem apresentação — carrega-a no Auto-Treino, Sessão {c.session_no || c.week}.
        </div>
      )}
      {c.sends && <SendLog sends={c.sends} hasCaptain={!!c.captain} />}
      {s?.attendance && <Attendance s={s} onChange={onChange} />}
      {s?.attendance && s.pending_confirmation && <ConfirmAttendance s={s} onChange={onChange} />}
      {s && c.can_manage && <NotHeld s={s} club={c.club} week={c.week} sessionNo={c.session_no} onChange={onChange} />}
      {c.can_manage && <CaptainPlan club={c.club} onChanged={onChange} />}
    </div>
  );
}

function QrButton({ s, club }) {
  const [open, setOpen] = useState(false);
  const url = `${window.location.origin}${s.checkin_path}`;
  return (
    <>
      <button onClick={() => setOpen(true)} className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5"
        data-testid={`session-qr-${club}`}><QrCode className="w-3.5 h-3.5" /> QR de presenças</button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white text-black p-8 max-w-md w-full text-center relative" onClick={(e) => e.stopPropagation()}
            data-testid="session-qr-modal">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-[#555]" data-testid="session-qr-close"><X className="w-5 h-5" /></button>
            <div className="text-[11px] tracking-[0.3em] font-bold text-[#E50000]">MARCA A TUA PRESENÇA</div>
            <div className="font-display text-2xl font-black uppercase mt-1">{s.title}</div>
            <div className="text-xs text-[#555] mb-4">{s.when_label}</div>
            <div className="flex justify-center"><QRCodeCanvas value={url} size={300} level="M" includeMargin /></div>
            <div className="flex justify-center gap-2 mt-4">
              <a href={`${API}${s.qr_png}`} download target="_blank" rel="noreferrer"
                className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 border border-[#ccc] inline-flex items-center gap-1.5"
                data-testid="session-qr-download"><Download className="w-3 h-3" /> Descarregar</a>
              <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copiado"); }}
                className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 border border-[#ccc] inline-flex items-center gap-1.5"
                data-testid="session-qr-copy"><Copy className="w-3 h-3" /> Copiar link</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NotHeld({ s, club, week, sessionNo, onChange }) {
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (body, msg) => {
    setBusy(true);
    try { await api.post(`/weekly-sessions/${s.id}/not-held`, body); toast.success(msg); setOpen(false); onChange(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const startHere = async (n) => {
    if (!window.confirm(`A reunião desta semana do clube ${club} passa a ser a Sessão ${n}. As seguintes seguem a partir daí. Confirmas?`)) return;
    setBusy(true);
    try {
      await api.post("/weekly-sessions/session-start", { club, week, session_no: n });
      toast.success(`Esta reunião passou a ser a Sessão ${n}`);
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="border-t border-[#222] pt-3 mt-3 space-y-2" data-testid={`session-nothheld-${club}`}>
      {!open ? (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setOpen(true)} className="kw-btn-secondary !py-1 !px-3 text-[10px]"
            data-testid={`session-not-held-${club}`}>A reunião não se realizou</button>
          <span className="text-[10px] uppercase tracking-widest text-[#737373]">Esta reunião é a</span>
          <select value={sessionNo || week} onChange={(e) => startHere(Number(e.target.value))} disabled={busy}
            className="bg-[#0B0B0B] border border-[#1F1F1F] text-xs px-2 py-1" data-testid={`session-no-select-${club}`}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>Sessão {n}</option>
            ))}
          </select>
          <span className="text-[10px] text-[#555]">(ajusta se o clube ficou atrasado)</span>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-[#A3A3A3]">
            Se a reunião não aconteceu, ninguém leva falta e a semana não conta presenças. Podes também remarcá-la para outro dia.
          </p>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (opcional)"
            className="w-full bg-[#0B0B0B] border border-[#1F1F1F] text-xs px-2 py-1" data-testid={`session-not-held-reason-${club}`} />
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => act({ reason }, "Reunião marcada como não realizada")} disabled={busy}
              className="kw-btn-primary !py-1 !px-3 text-[10px]" data-testid={`session-not-held-confirm-${club}`}>
              Não se realizou (sem faltas)
            </button>
            <span className="text-[10px] uppercase tracking-widest text-[#555]">ou remarcar para</span>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
              className="bg-[#0B0B0B] border border-[#1F1F1F] text-xs px-2 py-1" data-testid={`session-reschedule-when-${club}`} />
            <button onClick={() => when ? act({ reason, new_datetime: when }, "Reunião remarcada") : toast.error("Escolhe a nova data e hora")}
              disabled={busy} className="kw-btn-secondary !py-1 !px-3 text-[10px]" data-testid={`session-reschedule-${club}`}>
              Remarcar
            </button>
            <button onClick={() => setOpen(false)} className="text-[10px] uppercase text-[#737373]">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmAttendance({ s, onChange }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!window.confirm("Confirmar as presenças desta reunião? Quem ficar marcado como falta perde os pontos de presença.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/weekly-sessions/${s.id}/confirm-attendance`);
      toast.success(r.data.absences ? `Presenças confirmadas · ${r.data.absences} falta(s) lançada(s)` : "Presenças confirmadas");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <div className="border-t border-[#E50000]/40 mt-3 pt-3 space-y-2" data-testid={`session-pending-${s.id}`}>
      <p className="text-[11px] text-[#FF6B6B] leading-relaxed">
        A reunião terminou e as presenças ainda não foram confirmadas. Até confirmares, <b>ninguém leva falta</b>.
        Marca acima quem esteve presente (e quem chegou a horas) e só depois confirma.
      </p>
      <button onClick={go} disabled={busy} className="kw-btn-primary !py-1 !px-3 text-[10px]"
        data-testid={`session-confirm-attendance-${s.id}`}>
        {busy ? "A confirmar…" : "Confirmar presenças e fechar"}
      </button>
    </div>
  );
}

function Attendance({ s, onChange }) {
  const fix = async (r, present, punctual) => {
    try {
      await api.patch(`/weekly-sessions/${s.id}/attendance/${r.id}`, { present, punctual });
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!s.attendance.length) return <div className="text-xs text-[#737373]">Ainda não há coopetidores neste clube.</div>;
  return (
    <div className="border-t border-[#222] pt-3 divide-y divide-[#161616]" data-testid="session-attendance">
      {s.attendance.map((r) => (
        <div key={r.id} className="py-2 flex items-center gap-2 text-sm" data-testid={`session-row-${r.id}`}>
          <span className="flex-1 truncate">{r.name}</span>
          {r.source === "qr" && <span className="text-[9px] uppercase tracking-widest text-[#737373]">QR</span>}
          <button onClick={() => fix(r, !r.present, false)} title="Presente"
            className={`px-2 py-1 border text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 transition-colors ${r.present ? "border-[#22C55E] text-[#22C55E]" : "border-[#333] text-[#555]"}`}
            data-testid={`session-present-${r.id}`}><Check className="w-3 h-3" /> {r.present ? "Presente" : "Falta"}</button>
          <button onClick={() => r.present && fix(r, true, !r.punctual)} disabled={!r.present} title="Pontual (+5)"
            className={`px-2 py-1 border text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 transition-colors disabled:opacity-30 ${r.punctual ? "border-[#EAB308] text-[#EAB308]" : "border-[#333] text-[#555]"}`}
            data-testid={`session-punctual-${r.id}`}><Clock className="w-3 h-3" /> +5</button>
        </div>
      ))}
    </div>
  );
}

